import { useCallback, useRef, useState } from "react";

import { useObjectMemo } from "./useObjectMemo.hook";

// Ignores repeat calls to `handler` until the previous call settles, e.g. so a
// submit button can't re-fire while async work (like device fingerprinting)
// runs before the request starts and its `loading` state takes over.
export const useGuardedSubmit = <Args extends unknown[]>(
  handler: (...args: Args) => Promise<void> | void,
) => {
  const inFlight = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = useCallback(
    async (...args: Args) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setIsSubmitting(true);
      try {
        await handler(...args);
      } finally {
        inFlight.current = false;
        setIsSubmitting(false);
      }
    },
    [handler],
  );

  return useObjectMemo({ submit, isSubmitting });
};
